import { Injectable, Type } from '@angular/core';
import { ConfigService } from './config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { BaseWidgetComponent } from '../components/strategy-grid/base-widget/base-widget.component';
import { SafeHtml } from '@angular/platform-browser';

export namespace ChatService {
  export type ChatResponse = {
    output?: string;
    steps?: string[];
    error?: string;
    model?: string;
    streaming?: boolean;
  };

  export type HistoryResponse = {
    title: string;
    messages: ChatMessage[];
  };

  export type ChatSession = {
    title: string;
    session: string;
    mtime: number;
  };

  export type ListResponse = ChatSession[];

  export type RemoveResponse =
    | {
        success: true;
        error: undefined;
      }
    | {
        success: false;
        error: string;
      };

  export type ModelsResponse = {
    revisors: AvailableModel[];
    hasAllModels: boolean;
  };

  export type NewUUIDResponse = {
    uuid: string;
  };

  export type AvailableModel = {
    model: `${string}:${string}`;
    icon: 'deepseek' | 'ollama' | 'qwen' | 'mistral' | string | null;
    name: string;
    desc?: string; // Make description optional
  };

  export type ChatMessage = {
    isSelf: boolean;
    content: string | SafeHtml;
    isError?: boolean;
    title?: string;
    model?: string;
    isCollapsed?: boolean;
    lineBreak?: boolean;
    isAnchor?: boolean;
    isFinal?: boolean;
    isHint?: boolean;
    isStreaming?: boolean;
    timestamp?: string;
    componentType?: Type<BaseWidgetComponent>;
    componentInputs?: Record<string, any>; // Inputs to pass to the dynamic component
    widgetScreenshot?: string; // Base64 screenshot of widget for persistence
    messageId?: string; // Server message ID for updating metadata
    turnPath?: string; // Path to the turn file for easy reference in debugging
    pinned?: boolean; // Whether this message is pinned to the session
    reactions?: { emoji: string; reactor: 'user' | 'assistant'; timestamp: string }[];
    isMuted?: boolean; // Sleep/status event — renders as centered divider instead of message bubble
    sleepReason?: string; // Human-readable reason for entering sleep mode
    read?: boolean; // Read receipt: true = agent read it, false = delivered but unread
    handledIn?: string; // A follow-up folded into an earlier reply: that reply's message id
    handledAt?: string; // ISO time the agent acknowledged it
    sessionId?: string; // Claude CLI session ID for diagnostics
    durationMs?: number; // Time-to-respond in milliseconds (from stream_start to stream_end)
    channelActions?: { type: string; title: string; summary?: string; warm_up?: string; session_id?: string; agent_id?: string; dismissed?: boolean }[]; // Parsed channel-action blocks
    links?: string[]; // Knowledge graph wikilinks extracted from LINKS: footer
    agentName?: string; // Display name of the agent that authored this message
    contentTruncated?: boolean; // True when backend sent a preview instead of full content
    contentLength?: number; // Original content byte count (when truncated)
    dedupSig?: string; // Normalized content signature — collapses duplicate renders of one reply (replay/catch-up)
    presetAnswer?: string; // Chosen option label for an AskUserQuestion card in this turn — renders as the selected state
  };

  export type StreamEvent = {
    type: 'stream-start' | 'stream-token' | 'stream-end' | 'stream-error';
    session: string;
    message_id: string;
    content?: string;
    done?: boolean;
    timestamp: string;
    model?: string;
    origin: string;
    team?: string;
    error?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private _httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    timeout: 600000
  };

  constructor(private http: HttpClient) {
    (<any>window).$chat = this;
  }

  public chat = (team: string, username: string, uuid: string, msg: string, project: string, model?: string, stream: boolean = true) =>
    this.http.post<ChatService.ChatResponse>(`${ConfigService.chatUrl}/${team}/${username}/${uuid}`, { msg, project, model, stream });

  public chatAsync = async (team: string, username: string, uuid: string, msg: string, project: string, model?: string, stream: boolean = true) =>
    lastValueFrom(this.chat(team, username, uuid, msg, project, model, stream));

  public history = (team: string, username: string, uuid: string) =>
    this.http.get<ChatService.HistoryResponse>(`${ConfigService.chatUrl}/${team}/${username}/${uuid}`);

  public historyAsync = async (team: string, username: string, uuid: string) => lastValueFrom(this.history(team, username, uuid));

  public slash = (team: string, username: string, uuid: string, command: string, args: any) =>
    this.http.post<ChatService.ChatResponse>(`${ConfigService.chatUrl}/${team}/${username}/${uuid}/slash/${command}`, args, this._httpOptions );

  public slashAsync = async (team: string, username: string, uuid: string, command: string, args: any) =>
    lastValueFrom(this.slash(team, username, uuid, command, args));
    
  public cancelSlash = (commandId: string) =>
    this.http.post<ChatService.ChatResponse>(`${ConfigService.chatUrl}/cancel_command`, { commandId });
    
  public cancelSlashAsync = async (commandId: string) =>
    lastValueFrom(this.cancelSlash(commandId));
    
  public cancelChat = (chatId: string) =>
    this.http.post<ChatService.ChatResponse>(`${ConfigService.chatUrl}/cancel_chat`, { chatId });
    
  public cancelChatAsync = async (chatId: string) =>
    lastValueFrom(this.cancelChat(chatId));

  public remove = (team: string, username: string, uuid: string) =>
    this.http.delete<ChatService.RemoveResponse>(`${ConfigService.chatUrl}/${team}/${username}/${uuid}`);

  public removeAsync = async (team: string, username: string, uuid: string) => lastValueFrom(this.remove(team, username, uuid));

  public models = () => this.http.get<ChatService.ModelsResponse>(`${ConfigService.chatUrl}/models`);

  public modelsAsync = async () => lastValueFrom(this.models());

  public getNewUUID = () => this.http.get<ChatService.NewUUIDResponse>(`${ConfigService.chatUrl}/new_uuid`);

  public getNewUUIDAsync = async () => lastValueFrom(this.getNewUUID());

  public rename = (team: string, username: string, uuid: string, title: string) =>
    this.http.put<{ error?: string }>(`${ConfigService.chatUrl}/${team}/${username}/${uuid}`, { title });

  public renameAsync = async (team: string, username: string, uuid: string, title: string) =>
    lastValueFrom(this.rename(team, username, uuid, title));

  public list = (team: string, username: string) =>
    this.http.get<ChatService.ListResponse>(`${ConfigService.chatUrl}/${team}/${username}/list`);

  public listAsync = async (team: string, username: string) => lastValueFrom(this.list(team, username));

  public getModelData(
    team: string,
    username: string,
    uuid: string,
    model: string,
    interval: string,
    from: number,
    to: number,
    ensembleMethod: string,
    strats: string[] = []
  ) {
    let url = `${ConfigService.chatUrl}/${team}/${username}/${uuid}/model_history?model_name=${model}&interval=${interval}&from=${from}&to=${to}&ensemble_method=${ensembleMethod}`;
    if (strats && strats.length > 0) {
      url += `&strats=${strats.join(',')}`;
    }
    return this.http.get(url, this._httpOptions);
  }

  getCachedAnalysis = (team: string, username: string, uuid: string, ticker: string) =>
    this.http.get(`${ConfigService.chatUrl}/${team}/${username}/${uuid}/cache/analysis/${ticker}`);
}
